import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField
} from '@mui/material';
import '../styles/ManageUsers.css';

const ManageUsers = () => {
  const [selectedBranch, setSelectedBranch] = useState('');
  const [branches, setBranches] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, userId: null });
  const [editDialog, setEditDialog] = useState({
    open: false,
    userId: null,
    username: '',
    vehicleNumber: '',
    email: '',
    branch: ''
  });
  const [resetPasswordDialog, setResetPasswordDialog] = useState({ open: false, userId: null, username: '' });
  const { user: currentUser } = useAuth();

  // Format username for display (e.g. "michael.jenne" -> "Michael Jenne")
  const formatUsername = (username) => {
    if (!username) return '';
    return username
      .split('.')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  };

  // Fetch branches on component mount
  useEffect(() => {
    const loadBranches = async () => {
      try {
        const response = await apiService.getBranches();
        setBranches(response);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching branches:', err);
        setError('Failed to fetch branches');
        setLoading(false);
      }
    };
    loadBranches();
  }, []);

  // Fetch users when branch is selected
  useEffect(() => {
    const loadUsers = async () => {
      if (!selectedBranch) return;
      
      try {
        setLoading(true);
        setError('');
        const response = await apiService.getBranchUsers(selectedBranch);
        setUsers(response);
      } catch (err) {
        console.error('Error fetching users:', err);
        setError('Failed to fetch users for this branch');
      } finally {
        setLoading(false);
      }
    };

    if (selectedBranch) {
      loadUsers();
    }
  }, [selectedBranch]);

  const handleBranchChange = (event) => {
    setSelectedBranch(event.target.value);
    setError('');
    setSuccess('');
  };

  const handleEditClick = (user) => {
    setEditDialog({
      open: true,
      userId: user.id,
      username: user.username || '',
      vehicleNumber: user.vehicle_number || '',
      email: user.email || '',
      branch: user.branch || ''
    });
  };

  const handleEditClose = () => {
    setEditDialog({
      open: false,
      userId: null,
      username: '',
      vehicleNumber: '',
      email: '',
      branch: ''
    });
  };

  const handleEditSave = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Transform the data to match backend expectations
      const updateData = {
        username: editDialog.username,
        vehicle_number: editDialog.vehicleNumber,  // Change vehicleNumber to vehicle_number
        branch: editDialog.branch
      };

      await apiService.updateUser(editDialog.userId, updateData);

      // Refresh the users list
      const updatedUsers = await apiService.getBranchUsers(selectedBranch);
      setUsers(updatedUsers);
      
      setSuccess('User updated successfully');
      handleEditClose();
    } catch (err) {
      console.error('Error updating user:', err);
      setError(err.message || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (userId) => {
    setDeleteDialog({ open: true, userId });
  };

  const handleDeleteConfirm = async () => {
    try {
      setLoading(true);
      setError('');
      
      await apiService.deleteUser(deleteDialog.userId);
      
      // Refresh the users list
      const updatedUsers = await apiService.getBranchUsers(selectedBranch);
      setUsers(updatedUsers);
      
      setSuccess('User deleted successfully');
      setDeleteDialog({ open: false, userId: null });
    } catch (err) {
      console.error('Error deleting user:', err);
      setError(err.message || 'Failed to delete user');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPasswordClick = (user) => {
    setResetPasswordDialog({ 
      open: true, 
      userId: user.id,
      username: user.username
    });
  };

  const handleResetPasswordConfirm = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Call the API to reset password
      await apiService.resetUserPassword(resetPasswordDialog.userId);
      
      // Close the dialog and show success message
      setResetPasswordDialog({ open: false, userId: null, username: '' });
      setSuccess(`Password reset email sent to ${resetPasswordDialog.username}`);
    } catch (err) {
      console.error('Error resetting password:', err);
      setError(err.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !selectedBranch) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Manage Users
      </Typography>

      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel>Select Branch</InputLabel>
        <Select
          value={selectedBranch}
          label="Select Branch"
          onChange={handleBranchChange}
        >
          {branches.map((branch) => (
            <MenuItem key={branch.name} value={branch.name}>
              {branch.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {loading && selectedBranch ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : (
        selectedBranch && (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Username</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Unit Number</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => {
                  console.log('Rendering user row:', user);
                  return (
                  <TableRow key={user.id}>
                    <TableCell>{formatUsername(user.username)}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.vehicle_number}</TableCell>
                    <TableCell>{user.is_admin ? 'Admin' : 'User'}</TableCell>
                    <TableCell>
                      <Box className="actions-cell">
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => handleEditClick(user)}
                          size="small"
                          className="action-button"
                        >
                          Edit
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => handleResetPasswordClick(user)}
                          size="small"
                          className="reset-password-button"
                        >
                          Reset Password
                        </Button>
                        <Button
                          variant="contained"
                          color="error"
                          onClick={() => handleDeleteClick(user.id)}
                          disabled={user.id === currentUser?.id}
                          size="small"
                          className="action-button"
                        >
                          Delete
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                )})}
              </TableBody>
            </Table>
          </TableContainer>
        )
      )}

      {/* Edit Dialog */}
      <Dialog 
        open={editDialog.open} 
        onClose={handleEditClose}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Username"
            type="text"
            fullWidth
            value={editDialog.username}
            onChange={(e) => {
              const sanitizeUsername = (str) => String(str || '').replace(/[^A-Za-z\s]/g, '').trim().toUpperCase();
              setEditDialog({ ...editDialog, username: sanitizeUsername(e.target.value) });
            }}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
           label="Unit Number"
            type="text"
            fullWidth
            value={editDialog.vehicleNumber}
            onChange={(e) => setEditDialog({ ...editDialog, vehicleNumber: e.target.value })}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Branch</InputLabel>
            <Select
              value={editDialog.branch}
              label="Branch"
              onChange={(e) => setEditDialog({ ...editDialog, branch: e.target.value })}
            >
              {branches.map((branch) => (
                <MenuItem key={branch.name} value={branch.name}>
                  {branch.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            label="Email"
            type="email"
            fullWidth
            value={editDialog.email}
            disabled
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose} disabled={loading}>Cancel</Button>
          <Button 
            onClick={handleEditSave} 
            variant="contained" 
            color="primary"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, userId: null })}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this user? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, userId: null })} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog
        open={resetPasswordDialog.open}
        onClose={() => setResetPasswordDialog({ open: false, userId: null, username: '' })}
      >
        <DialogTitle>Confirm Password Reset</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to reset the password for {resetPasswordDialog.username}? 
            An email with password reset instructions will be sent to this user.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetPasswordDialog({ open: false, userId: null, username: '' })} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleResetPasswordConfirm} color="primary" variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Send Reset Email'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageUsers;